import dayjs from 'dayjs';

import {
  ChangeEvent,
  FC,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Checkbox,
  Grid,
  IconButton,
  Tooltip,
  lighten,
  rgbToHex
} from '@mui/material';
import MDBox from '@components/MDBox';
import MDTypography from '@components/MDTypography';

import { RootState } from '@store/store';
import { getChartTheme, getDateFormat } from '@utils/chart';
import { roundNumber } from '@utils/common';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  ADDED_HEATMAP,
  CLICKED_DOWNLOAD_TABLE,
  REMOVED_HEATMAP
} from '@constants/eventNames';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  dataTableTotalKeyword
} from '@constants/common';
import Watermark from '@components/Watermark';
import StickyTable from '@components/StickyTable';
import FaIcon from '@components/FaIcon';
import brandLogo from '@assets/images/ds-watermark-img.png';

interface optionItem {
  label: string;
  value: string;
}

interface HeatMapProps {
  isDate: boolean;
  isDateTime: boolean;
  isFilterActive?: boolean;
  tableData: any[];
  xAxisLabelsExcluded?: string[];
  dropDownValues: {
    chart_type: string;
    chart_operation: string;
    x_axis: string;
    y_axis: string;
    segment_field: string;
  };
  segmentFields: optionItem[];
  xAxisKey: string;
  watermarkProps: {
    isKPI: boolean;
    onClickRemoveWaterMark: () => void;
  };
  openHeatMap?: boolean;
  availableFields: optionItem[];
  primaryColor?: string;
  ref?: React.ForwardedRef<HTMLButtonElement>;
  handleHeatMapArrowClick?: () => void;
  showAsPercentage: boolean;
}

const HeatMap: FC<HeatMapProps> = forwardRef(
  (
    {
      isDate,
      isDateTime,
      isFilterActive = false,
      tableData = [],
      xAxisLabelsExcluded = [],
      dropDownValues,
      segmentFields,
      xAxisKey,
      availableFields,
      openHeatMap = true,
      primaryColor,
      watermarkProps,
      handleHeatMapArrowClick,
      showAsPercentage
    },
    ref
  ) => {
    const currentChartData = useSelector(
      (state: RootState) => state?.currentChartData
    );

    const subscriptionFlag = useSelector(
      (state: RootState) => state?.AppData?.subKeyFlag
    );

    const isRemoveWaterMarkEnabledByPlan = useSelector(
      (state: RootState) => state?.AppData?.currentPlanFeatures?.removeWaterMark
    );

    const showWatermark = subscriptionFlag && !isRemoveWaterMarkEnabledByPlan;

    const [downloadClicked, setDownloadClicked] = useState<boolean>(false);
    const [isCheckBoxClicked, setCheckBoxClicked] = useState<boolean>(true);
    const [valueToColorMap, setValueToColorMap] = useState<
      Record<number, string>
    >({});
    const [filteredData, setFilteredData] = useState<{ [key: string]: any }[]>(
      []
    );
    const [sumRecord, setSumRecord] = useState<{ [key: string]: any }>({});

    const xAxis = useMemo(
      () =>
        availableFields?.find((axis) => axis.value === dropDownValues.x_axis)
          ?.label,
      [segmentFields, dropDownValues]
    );

    const yAxis = useMemo(
      () =>
        availableFields?.find((axis) => axis.value === dropDownValues.y_axis),
      [segmentFields, dropDownValues]
    );

    const segment_field = useMemo(
      () =>
        segmentFields?.find(
          (segment) => segment.value === dropDownValues.segment_field
        ),
      [segmentFields, dropDownValues]
    );

    const isFiltered = isFilterActive && xAxisLabelsExcluded.length > 0;

    const keys = Object.keys(tableData?.[0] || {});

    const labelWidths: number[] = [];

    const xAxisIndex = keys.findIndex((key) => key === xAxisKey);

    keys.splice(xAxisIndex, 1);

    useEffect(() => {
      const rangeFilteredData: { [key: string]: any }[] = [];
      const sumRecord: { [key: string]: any } = {};

      keys.forEach((key) => {
        sumRecord[key] = 0;
      });

      tableData.forEach((item: { [x: string]: string }) => {
        if (!xAxisLabelsExcluded.includes(item[xAxisKey]) && isFiltered) {
          rangeFilteredData.push(item);
          keys.forEach((key) => {
            if (Number(item[key])) sumRecord[key] += item[key];
          });
        } else if (!isFiltered) {
          keys.forEach((key) => {
            if (Number(item[key])) sumRecord[key] += item[key];
          });
        }
      });

      setSumRecord(sumRecord);

      if (isFiltered) setFilteredData(rangeFilteredData);
    }, [tableData, isFiltered]);

    if (segment_field) keys.push(dataTableTotalKeyword);

    const processedData = useMemo(() => {
      const data = isFiltered ? [...filteredData] : [...tableData];

      if (
        (isFiltered && filteredData.length > 0) ||
        (!isFiltered && tableData.length > 0)
      )
        data.push(sumRecord);

      return data;
    }, [filteredData, sumRecord]);

    const dataWithDate = processedData.map((item) => {
      let totalValue = 0;

      if (segment_field) {
        const valueToRemove: number | string = item[xAxisKey];

        let values = Object.values(item);

        if (keys.length === values.length)
          values = values.filter((value: any) => value !== valueToRemove);

        totalValue = values.reduce(
          (accumulator: any, currentValue: any) => accumulator + currentValue,
          0
        ) as number;
      }

      const dateGroup = currentChartData?.config.date_group;

      return {
        ...item,
        [xAxisKey]:
          [
            DATE_GROUP.DAYOFWEEK,
            DATE_GROUP.DAYOFMONTH,
            DATE_GROUP.WEEKOFYEAR,
            DATE_GROUP.MONTHOFYEAR,
            DATE_TIME_GROUP.DAYOFWEEK,
            DATE_TIME_GROUP.MONTHOFYEAR,
            DATE_TIME_GROUP.WEEKOFYEAR,
            DATE_TIME_GROUP.DAYOFMONTH,
            DATE_TIME_GROUP.HOUROFDAY
          ].includes(dateGroup)
            ? item[xAxisKey] // No formatting for specified date groups
            : isDate
            ? dayjs(item[xAxisKey]).format(getDateFormat(dateGroup))
            : item[xAxisKey],
        [dataTableTotalKeyword]: totalValue
      };
    });

    if (dataWithDate.length) {
      dataWithDate[dataWithDate.length - 1][xAxisKey] = dataTableTotalKeyword;
      dataWithDate.map((item: { [x: string]: string }) => {
        {
          const canvas = document.createElement('canvas') as HTMLCanvasElement;

          const ctx = canvas.getContext('2d');

          if (ctx) ctx.font = '14px Open Sans';

          const width = ctx?.measureText(item[xAxisKey]).width || 0;

          labelWidths.push(width);
        }
      });
      if (
        currentChartData.config.chart_type === 'Bar' ||
        dropDownValues.chart_type === 'Bar'
      ) {
        const lastData = dataWithDate.splice(dataWithDate.length - 1);

        dataWithDate.reverse().push(...lastData);
      }
    }

    const finalData = useMemo(() => dataWithDate, [processedData, sumRecord]);

    const chart_operation =
      dropDownValues?.chart_operation?.charAt(0) +
      dropDownValues?.chart_operation?.slice(1)?.toLowerCase();

    const chartHeading = segment_field?.value
      ? `${chart_operation} of ${
          yAxis?.label === xAxis ? 'Rows' : yAxis?.label
        } per ${xAxis} grouped by ${segment_field?.label} `
      : `${chart_operation} of ${
          yAxis?.label === xAxis ? 'Rows' : yAxis?.label
        } per ${xAxis}`;

    const yCols = useMemo(
      () =>
        keys.map((key) => ({
          Header: segment_field ? key : `${chart_operation} of ${yAxis?.label}`,
          accessor: key,
          width: 50,
          Cell: ({ cell }: any) => (
            <MDBox
              padding='.5px 3px'
              overflow='hidden'
              textOverflow='ellipsis'
              maxWidth='130px'
              color=''
            >
              {roundNumber(cell.row.original?.[key], 2).toLocaleString() +
                (showAsPercentage ? '%' : '') || '0'}
            </MDBox>
          )
        })),
      [keys]
    );

    const { current: dataColumns } = useRef([
      {
        Header: xAxisKey,
        accessor: xAxisKey,
        width: Math.max(...labelWidths) + 20,
        sticky: 'left',
        Cell: ({ cell }: any) => (
          <Tooltip title={cell.row?.original?.[xAxisKey] || ''}>
            <MDBox color='' sx={{ overflowWrap: 'break-word' }} maxWidth={680}>
              {!isNaN(cell.row?.original?.[xAxisKey]) && !isDate && !isDateTime
                ? roundNumber(
                    cell.row?.original?.[xAxisKey],
                    2
                  ).toLocaleString()
                : cell.row?.original?.[xAxisKey] || ''}
            </MDBox>
          </Tooltip>
        )
      },
      ...yCols
    ]);

    useEffect(() => {
      if (isCheckBoxClicked) generateColors();
      else setValueToColorMap({});
    }, [isCheckBoxClicked, currentChartData, processedData]);

    const generateColors = () => {
      const allTableValues = new Set(
        (isFilterActive ? [...filteredData] : [...dataWithDate])
          .flatMap((data: any) =>
            Object.keys(data)
              .filter(
                (key: string) =>
                  key !== xAxisKey &&
                  key !== dataTableTotalKeyword &&
                  data[xAxisKey] !== dataTableTotalKeyword
              )
              .map((filteredKey: string) => data[filteredKey])
          )
          .sort((a, b) => b - a)
      );

      let currentColor =
        getChartTheme(currentChartData)?.[1] ??
        getChartTheme(currentChartData)?.[0] ??
        (primaryColor || '#f0a34a');

      const temporaryMap: Record<number, string> = {};

      Array.from(allTableValues).forEach((value: number, index: number) => {
        if (index === allTableValues.size - 1) {
          temporaryMap[value] = '#ffffff';

          return;
        }
        if (index === 0) {
          temporaryMap[value] = currentColor;

          return;
        }

        currentColor = rgbToHex(lighten(currentColor, 1 / allTableValues.size));
        temporaryMap[value] = currentColor;
      });

      setValueToColorMap(temporaryMap);
    };

    const handleDownloadClick = () => {
      setDownloadClicked(true);
      sendAmplitudeData(CLICKED_DOWNLOAD_TABLE, {
        Is_heatmap: isCheckBoxClicked ? 'On' : 'Off'
      });
    };

    const handleMouseLeave = () => {
      if (downloadClicked)
        setTimeout(() => {
          setDownloadClicked(false);
        }, 100);
    };

    return (
      <MDBox
        sx={({ palette }) => ({
          backgroundColor: palette.white.main,
          height: ref && !openHeatMap ? '22px' : 'fit-content',
          mb: 1,
          px: 1,
          borderRadius: ref ? '0.75rem' : ''
        })}
      >
        <MDBox
          width={'100%'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          pt={1}
        >
          {showWatermark && (
            <MDBox pt={0.75} pl={0.5}>
              <Watermark
                brandLogo={brandLogo}
                isPreview={false}
                {...watermarkProps}
              />
            </MDBox>
          )}
          {openHeatMap && (
            <MDBox
              flex={1}
              display={'flex'}
              justifyContent={'center'}
              pl={
                currentChartData.id
                  ? showWatermark
                    ? '43px'
                    : '130px'
                  : showWatermark
                  ? '60px'
                  : '147px'
              }
              pr={
                currentChartData.id
                  ? showWatermark
                    ? '162px'
                    : '45px'
                  : showWatermark
                  ? '205px'
                  : '88px'
              }
              width={showWatermark ? '50%' : '85%'}
            >
              <Tooltip title={chartHeading} placement='top'>
                <MDTypography
                  variant='sm'
                  fontWeight='bold'
                  color='secondary'
                  maxWidth='fit-content'
                  sx={{
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'center',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                  flex={1}
                >
                  {chartHeading}
                </MDTypography>
              </Tooltip>
            </MDBox>
          )}
          <MDBox
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {openHeatMap && (
              <MDBox pb={'5px'}>
                <Tooltip
                  title={`${
                    isCheckBoxClicked
                      ? 'Click to show table without Heatmap'
                      : 'Click to show table as Heatmap'
                  }`}
                  placement='top-start'
                >
                  <Checkbox
                    checked={isCheckBoxClicked}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setCheckBoxClicked((prev) => !prev);
                      sendAmplitudeData(
                        event.target.checked ? ADDED_HEATMAP : REMOVED_HEATMAP
                      );
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={`${
                    downloadClicked ? 'Coming Soon' : 'Download table'
                  }`}
                  placement='top-start'
                >
                  <IconButton
                    onMouseLeave={handleMouseLeave}
                    onClick={handleDownloadClick}
                  >
                    <FaIcon
                      typographyProps={{ variant: 'sm', color: 'secondary' }}
                      icon='download'
                    />
                  </IconButton>
                </Tooltip>
              </MDBox>
            )}
            {ref && (
              <IconButton onClick={handleHeatMapArrowClick} ref={ref}>
                <FaIcon
                  icon={['far', 'angle-up']}
                  typographyProps={{
                    color: 'dark',
                    variant: 'lgg',
                    textGradient: 'true'
                  }}
                  size='xs'
                />
              </IconButton>
            )}
          </MDBox>
        </MDBox>
        {openHeatMap ? (
          dataWithDate.length === 0 ? (
            <Grid
              sx={{
                flex: 1,
                textAlign: 'center',
                my: '15%'
              }}
            >
              <Grid item>
                <FontAwesomeIcon icon='warning' />
                <MDTypography variant='h4'>No Data Found</MDTypography>
              </Grid>
            </Grid>
          ) : (
            <Grid container mb={2} justifyContent={'center'}>
              <StickyTable
                columns={dataColumns}
                data={finalData}
                colorMap={valueToColorMap}
              />
            </Grid>
          )
        ) : (
          <></>
        )}
      </MDBox>
    );
  }
);

export default HeatMap;
